import { useMemo, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/cn'

export interface SearchableSelectOption {
  value: string
  label: string
}

export type SearchableSelectSize = 'xs' | 'md'

/** Padding/typography per size — mirrors Select's sizes. */
const SIZES: Record<SearchableSelectSize, { input: string; option: string; chevron: number }> = {
  xs: { input: 'pl-2 pr-6 py-1.5 text-xs', option: 'px-2 py-1.5 text-xs', chevron: 12 },
  md: { input: 'pl-3 pr-8 py-2 text-sm', option: 'px-3 py-2 text-sm', chevron: 15 },
}

/** Long lists are cut off here — searching narrows them down. */
const MAX_VISIBLE = 50

/**
 * A select with a search input: type to filter options by label.
 * Options are sorted by label (an empty-value option like "Unassigned" is
 * pinned to the top). Keyboard: arrows move, Enter picks, Escape closes.
 */
export default function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  size = 'md',
  className,
  onSearchChange,
  onOpen,
  loading = false,
}: {
  options: SearchableSelectOption[]
  value: string
  onChange: (value: string) => void
  placeholder?: string
  /** Prompt shown once the picker is open, to signal you can type to filter. */
  searchPlaceholder?: string
  size?: SearchableSelectSize
  /** Layout classes for the wrapper (w-full, flex-1, min-w-0, …). */
  className?: string
  /**
   * Server-driven mode: reports the typed query (and '' on close) so the caller can
   * fetch matching options. Disables the built-in label filter — the caller's options
   * are already the matches (e.g. matched by e-mail even when the label is a name).
   */
  onSearchChange?: (query: string) => void
  /** Fires the first time the picker is opened — lets callers lazily fetch options. */
  onOpen?: () => void
  /** Shows a "Loading…" hint instead of "No matches." while options are being fetched. */
  loading?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [highlighted, setHighlighted] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  const sorted = useMemo(() => {
    const s = [...options].sort((a, b) => a.label.localeCompare(b.label))
    // keep a "none" choice ('' value, e.g. Unassigned) on top regardless of label
    const emptyIdx = s.findIndex((o) => o.value === '')
    if (emptyIdx > 0) s.unshift(...s.splice(emptyIdx, 1))
    return s
  }, [options])

  const filtered = useMemo(() => {
    if (onSearchChange) return sorted // server-driven: options already match the query
    const q = query.trim().toLowerCase()
    return q ? sorted.filter((o) => o.label.toLowerCase().includes(q)) : sorted
  }, [sorted, query, onSearchChange])

  const visible = filtered.slice(0, MAX_VISIBLE)
  const selected = options.find((o) => o.value === value)

  function close() {
    setOpen(false)
    setQuery('')
    setHighlighted(0)
    onSearchChange?.('')
  }

  function pick(v: string) {
    onChange(v)
    close()
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      e.stopPropagation()
      close()
      inputRef.current?.blur()
      return
    }
    if (!open) {
      if (e.key !== 'Tab') setOpen(true)
      return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      const opt = visible[highlighted] ?? visible[0]
      if (opt) pick(opt.value)
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlighted((i) => Math.min(i + 1, visible.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlighted((i) => Math.max(i - 1, 0))
    }
  }

  return (
    <div
      className={cn('relative', className)}
      onBlur={(e) => {
        // close only when focus leaves the component entirely
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) close()
      }}
    >
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={open}
        value={open ? query : (selected?.label ?? '')}
        placeholder={open ? searchPlaceholder : placeholder}
        onFocus={() => {
          setOpen(true)
          onOpen?.()
        }}
        onChange={(e) => {
          setQuery(e.target.value)
          setHighlighted(0)
          if (!open) setOpen(true)
          onSearchChange?.(e.target.value)
        }}
        onKeyDown={onKeyDown}
        className={cn(
          'w-full bg-white border border-gray-200 rounded-lg text-brand-dark',
          'focus:outline-none focus:ring-2 focus:ring-brand-primary/40',
          'placeholder:text-gray-400',
          SIZES[size].input,
        )}
      />
      <ChevronDown
        size={SIZES[size].chevron}
        className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
      />

      {open && (
        <div
          // preventDefault keeps focus on the input for any press inside the dropdown
          // (options, scrollbar, footer) so onBlur doesn't close it before onClick
          onMouseDown={(e) => e.preventDefault()}
          // Grow past the (often narrow) trigger so long "Name (email)" options aren't cut off; each
          // option also carries a title tooltip as a fallback for very long values.
          className="absolute z-20 mt-1 left-0 min-w-full w-max max-w-[22rem] max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg"
        >
          {visible.length === 0 && (
            <p className={cn('text-gray-400', SIZES[size].option)}>
              {loading ? 'Loading…' : 'No matches.'}
            </p>
          )}
          {visible.map((o, i) => (
            <button
              key={o.value || '∅'}
              type="button"
              title={o.label}
              onClick={() => pick(o.value)}
              onMouseEnter={() => setHighlighted(i)}
              className={cn(
                'block w-full text-left truncate',
                SIZES[size].option,
                i === highlighted ? 'bg-brand-primary/10 text-brand-dark' : 'text-brand-dark',
                o.value === value && 'font-medium',
              )}
            >
              {o.label}
            </button>
          ))}
          {filtered.length > MAX_VISIBLE && (
            <p className={cn('text-gray-400 border-t border-gray-100', SIZES[size].option)}>
              Showing {MAX_VISIBLE} of {filtered.length} — type to narrow.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
